/** @jsxImportSource react */
import Spinner from '@react/shared/components/Spinner';
import { Button } from '@react/system';
import clsxm from '@sly/frontend/common/lib/clsxm';
import { Html, Share } from '@sly/icons/react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { MutableRefObject } from 'react';

const Modal = dynamic(
  () =>
    import(
      /* webpackChunkName: "chunkModal" */ '@react/shared/components/Modal'
    )
);

const ModalHeader = dynamic(
  () =>
    import(
      /* webpackChunkName: "chunkModalHeader" */ '@react/shared/components/Modal/header'
    )
);

import useGenerateChart from '../hooks/useGenerateChart';

const EmbedAndShare = ({
  chartContainer,
  snapshotContainer,
  disabled = false,
}: {
  chartContainer: MutableRefObject<HTMLDivElement | null>;
  snapshotContainer: MutableRefObject<HTMLDivElement | null>;
  disabled?: boolean;
}) => {
  const {
    handleGenerateChart,
    isLoadingEmbed,
    isLoadingShare,
    isEmbedModalOpen,
    isShareModalOpen,
    closeModal,
    isModalOpen,
    chartUrl,
    currentUrl,
    handleEmbedHtml,
    handleShare,
  } = useGenerateChart({
    chartContainer,
    snapshotContainer,
  });
  return (
    <>
      <div className='flex gap-4'>
        <Button
          className='font-b-s flex items-center justify-center py-s px-l font-medium md:px-s'
          onClick={() => handleGenerateChart('embed')}
          variant='neutral'
          disabled={disabled}
        >
          {isLoadingEmbed ? <Spinner /> : <Html className='mr-xs' size='s' />}
          Embed
        </Button>
        <Button
          className='font-b-s flex items-center justify-center py-s px-l font-medium md:px-s'
          onClick={() => handleGenerateChart('share')}
          variant='neutral'
          disabled={disabled}
        >
          {isLoadingShare ? <Spinner /> : <Share className='mr-xs' size='s' />}
          Share
        </Button>
      </div>
      {!!isModalOpen && (
        <Modal isOpen={!!isModalOpen} toggleModal={() => closeModal()}>
          <ModalHeader toggleModal={() => closeModal()}>
            <h3 className='font-t-m font-bold'>
              {isModalOpen === 'embed' ? 'Embed' : 'Share'}
            </h3>
          </ModalHeader>
          <hr className='mt-4' />

          <>
            <div
              className={clsxm(
                'px-4 py-6',
                isEmbedModalOpen && 'flex flex-col-reverse gap-4 '
              )}
            >
              {isEmbedModalOpen && (
                <div className='w-full space-y-2'>
                  <p>Copy and paste this into any HTML page</p>
                  <textarea
                    readOnly
                    className='w-full rounded border border-slate-lighter-90 py-2 px-4'
                  >
                    {`<a href="${currentUrl}"><img src="${chartUrl}" alt="Generated Chart" /></a>`}
                  </textarea>
                  <Button
                    className='w-full'
                    onClick={handleEmbedHtml}
                    variant='primary'
                  >
                    Copy HTML
                  </Button>
                </div>
              )}

              <div>
                {chartUrl && (
                  <Image
                    layout='raw'
                    width={600}
                    height={400}
                    className='object-contain'
                    src={chartUrl}
                    alt='Generated Chart'
                  />
                )}
              </div>
            </div>
            {isShareModalOpen && (
              <div className='flex items-center gap-4 bg-slate-lighter-95 p-6'>
                <input
                  className='font-b-s block h-11 w-full rounded border p-s outline-none'
                  value={currentUrl}
                  readOnly
                />
                <Button
                  onClick={handleShare}
                  variant='primary'
                  className='min-w-max'
                >
                  Copy Link
                </Button>
              </div>
            )}
          </>
        </Modal>
      )}
    </>
  );
};

export default EmbedAndShare;
